import React, { useState, useEffect } from "react";
import { Zap, ShieldCheck, Lock, AlertCircle } from "lucide-react";

export default function Login() {
    const [error, setError] = useState("");

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("error") === "unauthorized_domain") {
            setError("Access Denied: Only @querytel.com and @o365itguys.com accounts are permitted.");
        } else if (params.get("error")) {
            setError("Authentication failed. Please verify your Microsoft 365 account.");
        }
    }, []);

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0B1120] px-4 selection:bg-blue-600/30">
            <div className="w-full max-w-[420px]">
                {/* Logo & Brand */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-tr from-blue-700 to-indigo-500 mb-4 shadow-xl shadow-blue-600/25 border border-white/10">
                        <Zap className="w-7 h-7 text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-white tracking-tight">
                        QueryTel <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">Sentinel</span>
                    </h1>
                    <p className="text-xs text-slate-400 mt-1.5 font-medium tracking-wide">Next-Gen Autonomous SOC Platform</p>
                </div>

                {/* Login Card */}
                <div className="bg-[#111827]/90 backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8 shadow-2xl relative overflow-hidden">
                    {/* Top ambient glow */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent"></div>

                    {/* Security Badge */}
                    <div className="flex justify-center mb-5">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[11px] font-semibold tracking-wide">
                            <ShieldCheck className="w-3.5 h-3.5" />
                            Enterprise SSO Enforced
                        </span>
                    </div>

                    <div className="text-center mb-6">
                        <h2 className="text-base font-semibold text-white tracking-tight">Sign in with Enterprise Identity</h2>
                        <p className="text-xs text-slate-400 mt-1">
                            Password authentication is disabled. Sign in via your corporate Microsoft Entra ID account.
                        </p>
                    </div>

                    {error && (
                        <div className="mb-5 px-3.5 py-2.5 rounded-xl bg-rose-500/10 border border-rose-500/25 text-rose-400 text-xs font-medium flex items-center gap-2.5">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 text-rose-400" />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Microsoft 365 SSO Primary Action */}
                    <a
                        href="/auth/azure/login"
                        className="w-full flex items-center justify-center gap-3 py-3.5 px-4 bg-[#0078D4] hover:bg-[#006cc1] text-white text-sm font-semibold rounded-xl shadow-lg shadow-blue-600/20 transition-all hover:scale-[1.01] active:scale-[0.99] border border-white/10"
                    >
                        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 21 21">
                            <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                            <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                        </svg>
                        <span>Sign in with Microsoft 365</span>
                    </a>

                    {/* Security Notice / Domains */}
                    <div className="mt-7 pt-5 border-t border-white/[0.06] text-center">
                        <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 font-medium mb-1.5">
                            <Lock className="w-3 h-3 text-slate-500" /> Authorized Tenants Only
                        </div>
                        <p className="text-[11px] text-slate-400 font-mono">
                            @querytel.com • @o365itguys.com
                        </p>
                    </div>
                </div>

                <p className="text-center text-[11px] text-slate-600 mt-6 tracking-wide">
                    © 2026 QueryTel Inc. Protected by Zero-Trust Architecture.
                </p>
            </div>
        </div>
    );
}
